import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { 
  Button, 
  TextField, 
  Typography, 
  Paper, 
  Box, 
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { loginUser, clearError } from '../store/authSlice';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';

const LoginSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required'),
});

const Login = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector(state => state.auth);
  const [logoutMessage, setLogoutMessage] = React.useState<string | null>(null);

  // Check for auto-logout reason
  useEffect(() => {
    const reason = sessionStorage.getItem('logoutReason');
    if (reason === 'session_expired') {
      setLogoutMessage('Your session has expired. Please log in again.');
      sessionStorage.removeItem('logoutReason');
    } else if (reason === 'unauthorized') {
      setLogoutMessage('You have been logged out due to authentication failure. Please log in again.');
      sessionStorage.removeItem('logoutReason');
    }
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = (values: { username: string; password: string }) => {
    dispatch(loginUser(values));
  };

  return (
    <Container component="main" maxWidth="xs">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center' 
        }}>
          <Typography component="h1" variant="h5" sx={{ mb: 3 }}>
            Login to ABAD Admin
          </Typography>
          
          {logoutMessage && (
            <Alert severity="warning" sx={{ width: '100%', mb: 2 }} onClose={() => setLogoutMessage(null)}>
              {logoutMessage}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Formik
            initialValues={{ username: '', password: '' }}
            validationSchema={LoginSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched }) => (
              <Form style={{ width: '100%' }}>
                <Field
                  as={TextField}
                  fullWidth
                  id="username"
                  name="username"
                  label="Username"
                  margin="normal"
                  variant="outlined"
                  error={touched.username && !!errors.username}
                  helperText={<ErrorMessage name="username" />}
                  disabled={loading}
                />
                
                <Field
                  as={TextField}
                  fullWidth
                  id="password"
                  name="password"
                  label="Password"
                  type="password"
                  margin="normal"
                  variant="outlined"
                  error={touched.password && !!errors.password}
                  helperText={<ErrorMessage name="password" />}
                  disabled={loading}
                />
                
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 